// The devConsole object is a wrapper around the console object.
// It allows you to log messages to the console in development mode.
// In production, the console is disabled by default.
// To enable it, use devConsole.enable() in the console or in the browser console.

// ALWAYS USE devConsole.*() instead of console.*() in your code,
// to avoid leaking logs to production.

// In code usage:
//   import devConsole from "@utils/devConsole";
//   devConsole.error("Something went wrong: %s", "error message");

const CONSOLE_METHODS = ["log", "error", "warn", "info", "debug", "assert"] as const;
type ConsoleMethod = keyof Pick<Console, (typeof CONSOLE_METHODS)[number]>;

type ConsoleMethodTypes = {
  [K in ConsoleMethod]: Console[K];
};

const isDev = import.meta.env.NODE_ENV !== "production";

const CONSOLE_ENABLED_KEY = "debug_console_enabled";

const createNoopFunction = () => () => {};

const isConsoleEnabled = () => {
  return isDev || localStorage.getItem(CONSOLE_ENABLED_KEY) === "true";
};

const devConsole = {
  ...Object.keys(console).reduce((acc, method) => {
    if (CONSOLE_METHODS.includes(method as ConsoleMethod)) {
      acc[method as ConsoleMethod] = isConsoleEnabled()
        ? console[method as ConsoleMethod].bind(console)
        : createNoopFunction();
    }
    return acc;
  }, {} as ConsoleMethodTypes),

  // Helper methods to enable/disable console in production
  enable: () => {
    localStorage.setItem(CONSOLE_ENABLED_KEY, "true");
    window.location.reload(); // Reload to reinitialize console methods
  },
  disable: () => {
    localStorage.removeItem(CONSOLE_ENABLED_KEY);
    window.location.reload(); // Reload to reinitialize console methods
  },
  isEnabled: isConsoleEnabled,
};

export default devConsole;

declare global {
  interface Window {
    devConsole: typeof devConsole;
  }
}

window.devConsole = devConsole;
